import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { api } from "@/lib/api";
import type { Generation } from "@/types";

export interface CharacterCard {
  name: string;
  canonical_url: string | null;
  canonical_id: string | null;
  image_count: number;
  video_count: number;
  latest_at: string;
}

interface CharactersResponse {
  success: boolean;
  characters: CharacterCard[];
}

/**
 * Lists the user's characters — one card per distinct `folder` value
 * across their generations, with the most-recent IMAGE generation as
 * the canonical thumbnail.
 */
export function useCharacters() {
  const { user, isLoaded } = useUser();
  return useQuery({
    queryKey: ["characters", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user ID");
      return api.get<CharactersResponse>(`/characters/${user.id}`);
    },
    enabled: isLoaded && !!user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

interface RenameCharacterVariables {
  from: string;
  to: string;
}

interface SaveAsCharacterVariables {
  generationId: string;
  /** Optional user-chosen name; if omitted, the server picks "Character N". */
  name?: string;
}

interface SaveAsCharacterResponse {
  success: boolean;
  generation: Generation;
  /** The final name written (either the one the user chose or the auto-assigned "Character N"). */
  name: string;
}

/**
 * Promotes an unsaved character draft into a saved character. The
 * caller can pass a `name` (from the Save modal); otherwise the
 * server auto-picks the next "Character N". Either way the server
 * does the duplicate check + moderation so the frontend doesn't have
 * to fetch the character list first.
 */
export function useSaveAsCharacter() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ generationId, name }: SaveAsCharacterVariables) => {
      if (!user?.id) throw new Error("No user ID");
      return api.post<SaveAsCharacterResponse>(
        `/generations/${generationId}/save-as-character`,
        { user_id: user.id, ...(name ? { name } : {}) },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      queryClient.invalidateQueries({ queryKey: ["generation"] });
    },
  });
}

/**
 * Bulk-rename a character (updates `folder` for every owned generation
 * with the old name). The Character detail page calls this from the
 * inline rename pencil.
 */
export function useRenameCharacter() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RenameCharacterVariables) => {
      if (!user?.id) throw new Error("No user ID");
      return api.post<{ success: boolean }>(
        `/characters/${user.id}/rename`,
        vars,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

/**
 * Cascade-delete a character: soft-deletes every generation in the
 * folder. The character disappears from `useCharacters` and all asset
 * views automatically since they filter on `deleted_at IS NULL`.
 */
export function useDeleteCharacter() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      if (!user?.id) throw new Error("No user ID");
      return api.post<{ success: boolean }>(
        `/characters/${user.id}/delete`,
        { name },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}
